import { formatDate } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import type { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { resolveId } from '@seen/core/references'
import { Badge } from '@seen/ui'
import type { ReactNode } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconDue } from '#src/components/icons/IconDue.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'

interface DocumentReviewTaskBadgeProps {
  task: DocumentReviewTask
  /**
   * `full` (default): priority icon + avatar + "Review task" + due-date icon + formatted date.
   * `compact`: avatar + "Review" only. Use in dense layouts (e.g. participant Documents table).
   * Tooltip with title/assignee/due-date is identical in both variants.
   */
  variant?: 'full' | 'compact'
}

export function DocumentReviewTaskBadge({
  task,
  variant = 'full',
}: DocumentReviewTaskBadgeProps): ReactNode {
  const isFull = variant === 'full'
  const participantId = task.participant
    ? resolveId(task.participant)
    : undefined
  const taskUrl = participantId
    ? `/Patient/${participantId}/tasks/${task.id}`
    : undefined

  const badgeContent = (
    <Badge
      variant="outline"
      color="blue"
      className="flex w-fit cursor-pointer items-center gap-1 hover:bg-accent"
    >
      {isFull && (
        <TaskPriorityIcon
          priority={task.priority}
          isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
          className="size-3"
        />
      )}
      {task.ownerPractitioner ? (
        <Avatar profile={task.ownerPractitioner} size={3} />
      ) : (
        <IconUserPlaceholder className="size-3 text-muted-foreground" />
      )}
      <span>{isFull ? 'Review task' : 'Review'}</span>
      {isFull && task.dueOn && (
        <>
          <IconDue
            dateString={task.dueOn}
            isCompleted={task.isCompletedOrCancelled}
            className="size-3"
          />
          <span>{formatDate(task.dueOn)}</span>
        </>
      )}
    </Badge>
  )

  const tooltipContent = (
    <div className="flex flex-col gap-1 text-xs">
      <span className="font-medium">{task.title}</span>
      {referenceDisplay(task.ownerPractitioner) && (
        <span>Assigned to: {referenceDisplay(task.ownerPractitioner)}</span>
      )}
      {task.dueOn && <span>Due: {formatDate(task.dueOn)}</span>}
    </div>
  )

  if (!taskUrl) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badgeContent}</TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={taskUrl}
          onClick={(e) => e.stopPropagation()}
          className="no-underline"
        >
          {badgeContent}
        </Link>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  )
}
